// src/cases/ConfidisCaseCreate.js
import React from "react";
import {
  Create,
  SimpleForm,
  TextInput,
  DateInput,
  NumberInput,
  SelectInput,
  BooleanInput,
  RadioButtonGroupInput,
  required,
  email,
  minValue,
} from "react-admin";
import { Grid, Typography, Divider, Box } from '@mui/material';

import { getFirestore, collection, query, orderBy, limit, getDocs, doc, runTransaction } from 'firebase/firestore';
import { getTodayDatePrefix } from '../utils/caseIdUtils';
import { getCurrentDateISO } from '../utils/dateUtils';

const validateEmail = [required(), email()];
const validateRequired = [required()];
const validatePositiveNumber = [required(), minValue(0)];

// Custom Housing Type Selection Component with Radio Button Selection
const HousingTypeSelection = ({ source, label }) => {
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
          {label} (seleccione UNA opción que corresponda)
        </Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <RadioButtonGroupInput 
          source="gastosVivienda.tipoVivienda" 
          label="Tipo de Vivienda"
          choices={[
            { id: 'propiedadSinHipoteca', name: 'Propiedad sin hipoteca' },
            { id: 'propiedadConHipoteca', name: 'Propiedad con hipoteca' },
            { id: 'alquiler', name: 'Alquiler' },
            { id: 'otros', name: 'Otros' },
          ]}
          defaultValue=""
          fullWidth
        />
      </Grid>
    </Grid>
  );
};



// Function to get the next case number with date prefix
const getNextCofidisCaseNumber = async () => {
  const db = getFirestore();
  const datePrefix = getTodayDatePrefix();

  try {
    // Use a transaction to ensure atomic increment
    return await runTransaction(db, async (transaction) => {
      // Check if a counter document exists for today's date
      const counterRef = doc(db, 'counters', `confidisCaseNumber-${datePrefix}`);
      const counterDoc = await transaction.get(counterRef);

      let nextNumber = 1;

      if (counterDoc.exists()) {
        nextNumber = counterDoc.data().value + 1;
        transaction.update(counterRef, { value: nextNumber });
      } else {
        // If counter doesn't exist for today, find the highest existing case number for today
        const casesQuery = query(
          collection(db, 'cofidisCases'),
          orderBy('caseNumber', 'desc'),
          limit(50) // Check more cases to find today's cases
        );

        const snapshot = await getDocs(casesQuery);
        let highestTodayNumber = 0;

        // Look for cases created today
        snapshot.docs.forEach(doc => {
          const caseData = doc.data();
          if (caseData.caseNumber && typeof caseData.caseNumber === 'string' &&
              caseData.caseNumber.startsWith(datePrefix)) {
            // Extract the 8-digit number part after the date (YYYYMMDDXXXXXXXX)
            const numberPart = parseInt(caseData.caseNumber.slice(8) || '0');
            if (numberPart > highestTodayNumber) {
              highestTodayNumber = numberPart;
            }
          }
        });

        nextNumber = highestTodayNumber + 1;

        // Create the counter document for today
        transaction.set(counterRef, { value: nextNumber });
      }

      // Format as 8-digit number with leading zeros
      const formattedNumber = String(nextNumber).padStart(8, '0');
      return `${datePrefix}${formattedNumber}`;
    });
  } catch (error) {
    console.error('Error getting next confidis case number:', error);
    // Fallback: use timestamp-based number with date prefix
    const timestamp = Date.now();
    const fallbackNumber = String(timestamp % 100000000).padStart(8, '0');
    return `${datePrefix}${fallbackNumber}`;
  }
};

// Function to add statusChangeDate and caseNumber to the data
const transformCofidisData = async (data) => {
  const caseNumber = await getNextCofidisCaseNumber();

  // Clean undefined values from the data
  const cleanData = Object.fromEntries(
    Object.entries(data).filter(([_, value]) => value !== undefined)
  );

  // Transform housing data - ensure tipoVivienda is string and amounts are numbers
  let transformedData = { ...cleanData };
  
  if (transformedData.gastosVivienda) {
    transformedData.gastosVivienda = {
      tipoVivienda: transformedData.gastosVivienda.tipoVivienda || '',
      importeHipoteca: Number(transformedData.gastosVivienda.importeHipoteca) || 0,
      importeAlquiler: Number(transformedData.gastosVivienda.importeAlquiler) || 0,
    };
  }

  const newData = {
    ...transformedData,
    statusChangeDate: getCurrentDateISO(),
    caseNumber: caseNumber,
    bankGroup: 'COFIDIS', // Always set to COFIDIS for these cases
  };
  console.log('Create Confidis transformData:', newData);
  return newData;
};

// Section title component
const SectionTitle = ({ title }) => (
  <Box mt={3} mb={2}>
    <Typography variant="h6" color="primary">
      {title}
    </Typography>
    <Divider />
  </Box>
);

export const CofidisCaseCreate = (props) => (
      <Create {...props} transform={transformCofidisData}>
    <SimpleForm redirect="list">
      {/* DATOS DEL PRIMER TITULAR (First Holder's Data) */}
      <SectionTitle title="DATOS DEL PRIMER TITULAR" />
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <TextInput source="primerTitular.nombre" label="Nombre" validate={validateRequired} fullWidth />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextInput source="primerTitular.apellidos" label="Apellidos" validate={validateRequired} fullWidth />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextInput source="primerTitular.dni" label="DNI" validate={validateRequired} fullWidth />
        </Grid>
        <Grid item xs={12} md={4}>
          <DateInput source="primerTitular.fechaNacimiento" label="Fecha de nacimiento" validate={validateRequired} fullWidth />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextInput source="primerTitular.nacionalidad" label="Nacionalidad" validate={validateRequired} fullWidth />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextInput source="primerTitular.direccionPostal" label="Dirección postal" validate={validateRequired} fullWidth />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextInput source="primerTitular.poblacion" label="Población" validate={validateRequired} fullWidth />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextInput source="primerTitular.provincia" label="Provincia" validate={validateRequired} fullWidth />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextInput source="primerTitular.codigoPostal" label="Código Postal" validate={validateRequired} fullWidth />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextInput source="primerTitular.telefonoFijo" label="Teléfono fijo" fullWidth />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextInput source="primerTitular.movil" label="Móvil" validate={validateRequired} fullWidth />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextInput source="primerTitular.email" label="E-mail" validate={validateEmail} fullWidth />
        </Grid>
      </Grid>

      {/* DATOS DEL SEGUNDO TITULAR (Second Holder's Data) */}
      <SectionTitle title="DATOS DEL SEGUNDO TITULAR" />
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <TextInput source="segundoTitular.nombre" label="Nombre" fullWidth />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextInput source="segundoTitular.apellidos" label="Apellidos" fullWidth />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextInput source="segundoTitular.dni" label="DNI" fullWidth />
        </Grid>
        <Grid item xs={12} md={4}>
          <DateInput source="segundoTitular.fechaNacimiento" label="Fecha de nacimiento" fullWidth />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextInput source="segundoTitular.nacionalidad" label="Nacionalidad" fullWidth />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextInput source="segundoTitular.direccionPostal" label="Dirección postal" fullWidth />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextInput source="segundoTitular.poblacion" label="Población" fullWidth />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextInput source="segundoTitular.provincia" label="Provincia" fullWidth />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextInput source="segundoTitular.codigoPostal" label="Código Postal" fullWidth />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextInput source="segundoTitular.telefonoFijo" label="Teléfono fijo" fullWidth />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextInput source="segundoTitular.movil" label="Móvil" fullWidth />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextInput source="segundoTitular.email" label="E-mail" fullWidth />
        </Grid>
      </Grid>

      {/* DATOS PROFESIONALES (PRIMER TITULAR) */}
      <SectionTitle title="DATOS PROFESIONALES (PRIMER TITULAR)" />
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <TextInput source="primerTitular.profesion" label="Profesión" fullWidth />
        </Grid>
        <Grid item xs={12} md={4}>
          <NumberInput 
            source="primerTitular.salarioNetoMensual" 
            label="Salario neto mensual" 
            validate={validatePositiveNumber}
            defaultValue={0}
            fullWidth 
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <NumberInput 
            source="primerTitular.numeroPagas" 
            label="Número de pagas" 
            validate={validatePositiveNumber}
            defaultValue={12}
            min={1}
            max={16}
            fullWidth 
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SelectInput
            source="primerTitular.tipoContrato"
            label="Tipo de contrato"
            choices={[
              { id: 'Indefinido', name: 'Indefinido' },
              { id: 'Temporal', name: 'Temporal' },
              { id: 'Autónomo', name: 'Autónomo' },
              { id: 'Desempleado', name: 'Desempleado' },
              { id: 'Jubilado', name: 'Jubilado' },
            ]}
            defaultValue="Indefinido"
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextInput source="primerTitular.antiguedadLaboral" label="Antigüedad laboral (mes/año)" fullWidth />
        </Grid>
      </Grid>

      {/* DATOS PROFESIONALES (SEGUNDO TITULAR) */}
      <SectionTitle title="DATOS PROFESIONALES (SEGUNDO TITULAR)" />
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <TextInput source="segundoTitular.profesion" label="Profesión" fullWidth />
        </Grid>
        <Grid item xs={12} md={4}>
          <NumberInput 
            source="segundoTitular.salarioNetoMensual" 
            label="Salario neto mensual" 
            validate={validatePositiveNumber}
            defaultValue={0}
            fullWidth 
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <NumberInput 
            source="segundoTitular.numeroPagas" 
            label="Número de pagas" 
            validate={validatePositiveNumber}
            defaultValue={12}
            min={1}
            max={16}
            fullWidth 
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SelectInput
            source="segundoTitular.tipoContrato"
            label="Tipo de contrato"
            choices={[
              { id: 'Indefinido', name: 'Indefinido' },
              { id: 'Temporal', name: 'Temporal' },
              { id: 'Autónomo', name: 'Autónomo' },
              { id: 'Desempleado', name: 'Desempleado' },
              { id: 'Jubilado', name: 'Jubilado' },
            ]}
            defaultValue="Indefinido"
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextInput source="segundoTitular.antiguedadLaboral" label="Antigüedad laboral (mes/año)" fullWidth />
        </Grid>
      </Grid>

      {/* DATOS DE VIVIENDA (Housing Data) */}
      <SectionTitle title="DATOS DE VIVIENDA" />
      <HousingTypeSelection source="gastosVivienda" label="Tipos de Vivienda" />
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <NumberInput 
            source="gastosVivienda.importeAlquiler" 
            label="Importe Alquiler" 
            validate={validatePositiveNumber}
            defaultValue={0}
            fullWidth 
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <NumberInput 
            source="gastosVivienda.importeHipoteca" 
            label="Importe Hipoteca" 
            validate={validatePositiveNumber}
            defaultValue={0}
            fullWidth 
          />
        </Grid>
      </Grid>

      {/* OTROS DATOS (Other Data) */}
      <SectionTitle title="OTROS DATOS" />
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <SelectInput
            source="otrosDatos.estadoCivil"
            label="Estado civil"
            choices={[
              { id: 'Soltero', name: 'Soltero' },
              { id: 'Casado', name: 'Casado' },
              { id: 'Divorciado', name: 'Divorciado' },
              { id: 'Viudo', name: 'Viudo' },
              { id: 'Pareja de hecho', name: 'Pareja de hecho' },
            ]}
            defaultValue="Soltero"
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <NumberInput 
            source="otrosDatos.numeroHijos" 
            label="Nº hijos a su cargo" 
            validate={[minValue(0)]}
            defaultValue={0}
            fullWidth 
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextInput source="otrosDatos.otrosCreditosPrestamos" label="Otros créditos/préstamos" fullWidth />
        </Grid>
        <Grid item xs={12} md={4}>
          <NumberInput 
            source="otrosDatos.importeMensual" 
            label="Importe mensual" 
            validate={validatePositiveNumber}
            defaultValue={0}
            fullWidth 
          />
        </Grid>
      </Grid>

      {/* Case Information Section */}
      <SectionTitle title="Información del Caso" />
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <DateInput source="registeredDate" label="Fecha de registro" validate={validateRequired} defaultValue={new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Madrid' })} fullWidth />
        </Grid>
        <Grid item xs={12} md={4}>
          <SelectInput
            source="status"
            label="Estado"
            choices={[
              { id: 'En Revisión', name: 'En Revisión' },
              { id: 'Aprobado', name: 'Aprobado' },
              { id: 'Rechazado', name: 'Rechazado' },
            ]}
            defaultValue="En Revisión"
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextInput source="bankGroup" label="Grupo Bancario" defaultValue="COFIDIS" fullWidth />
        </Grid>
      </Grid>

      {/* Notes Section */}
      <SectionTitle title="Notas" />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextInput source="note" label="Notas" multiline rows={4} fullWidth />
        </Grid>
      </Grid>

      <TextInput source="statusChangeDate" style={{ display: 'none' }} />
    </SimpleForm>
  </Create>
); 